import type {
  InteractionMetrics,
  InteractionSubmission,
} from '../domain/life-state'
import { LifeClient, LifeClientError } from '../api/life-client'
import type { InteractionSample } from '../input/interaction-engine'
import { InteractionEngine } from '../input/interaction-engine'

const WINDOW_MS = 60_000
const VISITOR_KEY = 'lvirous.life.visitor.v1'
const QUEUE_KEY = 'lvirous.life.queue.v1'
const MAX_QUEUE_LENGTH = 5

export type InteractionSubmitter = (
  metrics: InteractionMetrics,
  windowStartedAt: string,
) => Promise<void>

interface QueuedInteraction {
  windowStartedAt: string
  metrics: InteractionMetrics
}

type InteractionStorage = Pick<Storage, 'getItem' | 'setItem'>

function createMemoryStorage(): InteractionStorage {
  const values = new Map<string, string>()
  return {
    getItem: (key) => values.get(key) ?? null,
    setItem: (key, value) => {
      values.set(key, value)
    },
  }
}

function resolveInteractionStorage(): InteractionStorage {
  try {
    return globalThis.localStorage ?? createMemoryStorage()
  } catch {
    return createMemoryStorage()
  }
}

function monotonicNow(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now()
}

function hasActivity(metrics: InteractionMetrics): boolean {
  return metrics.gentleMs > 0
    || metrics.activeBursts > 0
    || metrics.interactionKinds > 0
    || metrics.presenceMs > 0
}

function capMetrics(metrics: InteractionMetrics): InteractionMetrics {
  return {
    gentleMs: Math.min(60_000, Math.max(0, Math.round(metrics.gentleMs))),
    activeBursts: Math.min(120, Math.max(0, Math.round(metrics.activeBursts))),
    interactionKinds: Math.min(4, Math.max(0, Math.round(metrics.interactionKinds))),
    presenceMs: Math.min(60_000, Math.max(0, Math.round(metrics.presenceMs))),
  }
}

function mergeMetrics(
  first: InteractionMetrics,
  second: InteractionMetrics,
): InteractionMetrics {
  return capMetrics({
    gentleMs: first.gentleMs + second.gentleMs,
    activeBursts: first.activeBursts + second.activeBursts,
    interactionKinds: first.interactionKinds + second.interactionKinds,
    presenceMs: first.presenceMs + second.presenceMs,
  })
}

export class OfflineInteractionDelivery {
  private memoryToken: string | null = null

  constructor(
    private readonly client: Pick<LifeClient, 'submit'>,
    private readonly storage: InteractionStorage = resolveInteractionStorage(),
    private readonly createUuid: () => string = () => crypto.randomUUID(),
    private readonly onDelivered: () => void = () => undefined,
  ) {}

  async submit(metrics: InteractionMetrics, windowStartedAt: string): Promise<void> {
    if (!hasActivity(metrics)) {
      return
    }

    const queued = this.loadQueue()
    const queueCleared = await this.drain(queued)
    if (!queueCleared) {
      this.enqueue(queued, { windowStartedAt, metrics: capMetrics(metrics) })
      this.saveQueue(queued)
      return
    }

    const current = { windowStartedAt, metrics: capMetrics(metrics) }
    try {
      await this.client.submit(this.withToken(current))
      this.onDelivered()
    } catch (error) {
      if (isRetryable(error)) {
        this.enqueue(queued, current)
      }
      this.saveQueue(queued)
    }
  }

  async flushQueued(): Promise<void> {
    const queued = this.loadQueue()
    await this.drain(queued)
    this.saveQueue(queued)
  }

  private async drain(queue: QueuedInteraction[]): Promise<boolean> {
    while (queue.length > 0) {
      const oldest = queue[0]
      if (!oldest) {
        break
      }
      try {
        await this.client.submit(this.withToken(oldest))
        queue.shift()
        this.saveQueue(queue)
        this.onDelivered()
      } catch (error) {
        if (isRetryable(error)) {
          return false
        }
        queue.shift()
        this.saveQueue(queue)
      }
    }
    return true
  }

  private withToken(queued: QueuedInteraction): InteractionSubmission {
    return {
      visitorToken: this.getVisitorToken(),
      windowStartedAt: queued.windowStartedAt,
      metrics: queued.metrics,
    }
  }

  private getVisitorToken(): string {
    if (this.memoryToken) {
      return this.memoryToken
    }

    try {
      const stored = this.storage.getItem(VISITOR_KEY)
      if (stored && isUuid(stored)) {
        this.memoryToken = stored
        return stored
      }
    } catch {
      // Privacy modes may deny storage; the in-memory token still isolates the visitor.
    }

    this.memoryToken = this.createUuid()
    try {
      this.storage.setItem(VISITOR_KEY, this.memoryToken)
    } catch {
      // The token remains stable for this page lifecycle.
    }
    return this.memoryToken
  }

  private enqueue(queue: QueuedInteraction[], interaction: QueuedInteraction): void {
    queue.push(interaction)
    while (queue.length > MAX_QUEUE_LENGTH) {
      const first = queue.shift()
      const second = queue.shift()
      if (!first || !second) {
        break
      }
      queue.unshift({
        windowStartedAt: first.windowStartedAt,
        metrics: mergeMetrics(first.metrics, second.metrics),
      })
    }
  }

  private loadQueue(): QueuedInteraction[] {
    try {
      const value = JSON.parse(this.storage.getItem(QUEUE_KEY) ?? '[]') as unknown
      if (!Array.isArray(value)) {
        return []
      }
      return value
        .filter(isQueuedInteraction)
        .slice(-MAX_QUEUE_LENGTH)
        .map((item) => ({
          windowStartedAt: item.windowStartedAt,
          metrics: capMetrics(item.metrics),
        }))
    } catch {
      return []
    }
  }

  private saveQueue(queue: QueuedInteraction[]): void {
    try {
      this.storage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(0, MAX_QUEUE_LENGTH)))
    } catch {
      // A denied storage write must not interrupt the organism experience.
    }
  }
}

export class LifeSession {
  private windowHandle: ReturnType<typeof setInterval> | null = null

  constructor(
    private readonly engine: InteractionEngine,
    private readonly submit: InteractionSubmitter,
    private readonly now: () => number = monotonicNow,
    private readonly wallNow: () => number = () => Date.now(),
  ) {}

  start(): void {
    if (this.windowHandle !== null) {
      return
    }

    this.windowHandle = globalThis.setInterval(() => {
      void this.flush().catch(() => undefined)
    }, WINDOW_MS)
  }

  observe(sample: InteractionSample): void {
    this.engine.observe(sample)
  }

  async flush(): Promise<void> {
    const metrics = this.engine.flush(this.now())
    if (!hasActivity(metrics)) {
      return
    }
    const windowStartedAt = new Date(
      this.wallNow() - Math.min(WINDOW_MS, metrics.presenceMs),
    ).toISOString()
    await this.submit(metrics, windowStartedAt)
  }

  async stop(): Promise<void> {
    if (this.windowHandle !== null) {
      globalThis.clearInterval(this.windowHandle)
      this.windowHandle = null
    }
    await this.flush()
  }
}

function isRetryable(error: unknown): boolean {
  return error instanceof LifeClientError && error.retryable
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
}

function isQueuedInteraction(value: unknown): value is QueuedInteraction {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const queued = value as Partial<QueuedInteraction>
  const metrics = queued.metrics as Partial<InteractionMetrics> | undefined
  return typeof queued.windowStartedAt === 'string'
    && !Number.isNaN(Date.parse(queued.windowStartedAt))
    && typeof metrics === 'object'
    && metrics !== null
    && Number.isFinite(metrics.gentleMs)
    && Number.isFinite(metrics.activeBursts)
    && Number.isFinite(metrics.interactionKinds)
    && Number.isFinite(metrics.presenceMs)
}
